// src/modules/admin/components/UserEditModal/index.js
import React, { useState, useEffect, useCallback } from 'react';
import {
    Modal,
    Nav,
    Tab,
    Form,
    Button,
    Alert,
    Spinner,
} from 'react-bootstrap';
import PropTypes from 'prop-types';
import { useAuth } from '../../../../shared/hooks/useAuth';
import apiService from '../../../../shared/services/apiService';
import { preprocessImage } from '../../../../shared/utils/imageUtils';
import { ALL_PERMISSIONS } from '../../../../shared/constants/permissions';
import './styles.css';

const UserEditModal = ({ show, user, onSave, onClose }) => {
    const [activeTab, setActiveTab] = useState('profile');
    const [profileForm, setProfileForm] = useState({
        firstName: '',
        lastName: '',
        email: '',
    });
    const [profilePictureFile, setProfilePictureFile] = useState(null);
    const [profilePicturePreview, setProfilePicturePreview] = useState(null);
    const [profilePictureRemoved, setProfilePictureRemoved] = useState(false);
    const [pictureUploading, setPictureUploading] = useState(false);
    const [selectedPerms, setSelectedPerms] = useState([]);
    const [isSaving, setIsSaving] = useState(false);
    const [saveResult, setSaveResult] = useState(null);
    const [emailError, setEmailError] = useState('');

    const { hasPermission } = useAuth();
    const canEditProfile = hasPermission('users:editProfile');
    const canManagePermissions = hasPermission('users:managePermissions');

    const resetFormState = useCallback(() => {
        setProfileForm({
            firstName: user?.firstName || '',
            lastName: user?.lastName || '',
            email: user?.email || '',
        });
        setSelectedPerms(user?.permissions ?? []);
        setIsSaving(false);
        setSaveResult(null);
        setEmailError('');
        setProfilePictureFile(null);
        setProfilePictureRemoved(false);
        setPictureUploading(false);
        if (profilePicturePreview) {
            URL.revokeObjectURL(profilePicturePreview);
        }
        setProfilePicturePreview(null);
    }, [user, profilePicturePreview]);

    useEffect(() => {
        if (show && user) {
            resetFormState();
        }
    }, [show, user, resetFormState]);

    useEffect(() => {
        return () => {
            if (profilePicturePreview) {
                URL.revokeObjectURL(profilePicturePreview);
            }
        };
    }, [profilePicturePreview]);

    const handleProfileChange = (e) => {
        const { name, value } = e.target;
        setProfileForm(prev => ({ ...prev, [name]: value }));
        if (name === 'email') {
            setEmailError(validateEmail(value));
        }
    };

    const validateEmail = (email) => {
        if (!email) return '';
        const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!re.test(email)) {
            return 'Please enter a valid email address';
        }
        return '';
    };

    const handlePermToggle = (key) => {
        setSelectedPerms(prev =>
            prev.includes(key) ? prev.filter(p => p !== key) : [...prev, key]
        );
    };

    const handleProfilePictureChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setProfilePictureFile(file);
            setProfilePictureRemoved(false);
            setProfilePicturePreview(URL.createObjectURL(file));
        }
    };

    const handleRemoveProfilePicture = () => {
        setProfilePictureFile(null);
        setProfilePictureRemoved(true);
        setProfilePicturePreview(null);
    };

    const handleSaveProfile = async () => {
        if (!canEditProfile) return;
        if (emailError) return;

        setIsSaving(true);
        setSaveResult(null);

        try {
            const updates = {};
            if (profileForm.firstName !== user.firstName) updates.firstName = profileForm.firstName;
            if (profileForm.lastName !== user.lastName) updates.lastName = profileForm.lastName;
            if (profileForm.email !== user.email) updates.email = profileForm.email;

            // Handle profile picture upload
            if (profilePictureFile) {
                try {
                    setPictureUploading(true);

                    // Step 0: Preprocess image client-side (square crop, max 800x800, JPEG)
                    const processedFile = await preprocessImage(profilePictureFile, {
                        purpose: 'profile',
                        maxDimension: 800,
                        quality: 0.8
                    });

                    // Step 1: Get presigned URL (with targetUsername for admin upload)
                    const presignedResponse = await apiService.post('/attachments/presigned-url', {
                        fileName: processedFile.name,
                        contentType: processedFile.type,
                        temp: false,
                        targetUsername: user.username,
                        purpose: 'profile'
                    });

                    if (!presignedResponse.url || !presignedResponse.key) {
                        throw new Error('Failed to get presigned URL');
                    }

                    const { url: presignedUrl, key: s3Key } = presignedResponse;

                    // Step 2: Upload to S3
                    await new Promise((resolve, reject) => {
                        const xhr = new XMLHttpRequest();
                        xhr.open('PUT', presignedUrl, true);
                        xhr.setRequestHeader('Content-Type', processedFile.type);

                        xhr.onload = function() {
                            if (xhr.status >= 200 && xhr.status < 300) {
                                resolve(true);
                            } else {
                                reject(new Error('Upload failed: ' + xhr.status));
                            }
                        };

                        xhr.onerror = function() {
                            reject(new Error('Upload failed'));
                        };

                        xhr.send(processedFile);
                    });

                    updates.profilePicture = s3Key;
                } catch (uploadError) {
                    console.error('Error uploading profile picture:', uploadError);
                    setSaveResult({ type: 'error', message: 'Failed to upload profile picture' });
                    setPictureUploading(false);
                    setIsSaving(false);
                    return;
                } finally {
                    setPictureUploading(false);
                }
            }

            // Handle profile picture removal
            if (profilePictureRemoved) {
                updates.profilePicture = null;
            }

            await onSave(user.username, updates);
            setSaveResult({ type: 'success', message: 'Profile updated successfully' });
        } catch (err) {
            setSaveResult({ type: 'error', message: err.message || 'Failed to update profile' });
        } finally {
            setIsSaving(false);
        }
    };

    const handleSavePermissions = async () => {
        if (!canManagePermissions) return;
        setIsSaving(true);
        setSaveResult(null);

        try {
            await onSave(user.username, { permissions: selectedPerms });
            setSaveResult({ type: 'success', message: 'Permissions updated successfully' });
        } catch (err) {
            setSaveResult({ type: 'error', message: err.message || 'Failed to update permissions' });
        } finally {
            setIsSaving(false);
        }
    };

    const handleSave = async () => {
        if (activeTab === 'profile') {
            await handleSaveProfile();
        } else {
            await handleSavePermissions();
        }
    };

    const handleClose = () => {
        resetFormState();
        setActiveTab('profile');
        onClose();
    };

    return (
        <Modal
            show={show}
            onHide={handleClose}
            size="lg"
            centered
            className="user-edit-modal"
        >
            <Modal.Header closeButton>
                <Modal.Title>
                    <i className="bi bi-person-gear me-2"></i>
                    Edit User: {user?.username}
                </Modal.Title>
            </Modal.Header>

            <Modal.Body className="user-edit-modal-body">
                {saveResult && (
                    <Alert
                        variant={saveResult.type === 'success' ? 'success' : 'danger'}
                        className="mb-3"
                        dismissible
                        onClose={() => setSaveResult(null)}
                    >
                        {saveResult.message}
                    </Alert>
                )}

                <Tab.Container activeKey={activeTab} onSelect={setActiveTab}>
                    <Nav variant="tabs" className="mb-3 user-edit-nav">
                        <Nav.Item>
                            <Nav.Link eventKey="profile">
                                <i className="bi bi-person me-1"></i>
                                Profile
                            </Nav.Link>
                        </Nav.Item>
                        <Nav.Item>
                            <Nav.Link eventKey="permissions">
                                <i className="bi bi-shield-lock me-1"></i>
                                Permissions
                            </Nav.Link>
                        </Nav.Item>
                    </Nav>

                    <Tab.Content>
                        <Tab.Pane eventKey="profile">
                            {!canEditProfile && (
                                <Alert variant="warning" className="mb-3">
                                    <i className="bi bi-exclamation-triangle me-2"></i>
                                    You need the <strong>users:editProfile</strong> permission to edit this user's profile.
                                </Alert>
                            )}
                            <Form>
                                <Form.Group className="mb-3">
                                    <Form.Label>First Name</Form.Label>
                                    <Form.Control
                                        type="text"
                                        name="firstName"
                                        value={profileForm.firstName}
                                        onChange={handleProfileChange}
                                        disabled={!canEditProfile}
                                    />
                                </Form.Group>

                                <Form.Group className="mb-3">
                                    <Form.Label>Last Name</Form.Label>
                                    <Form.Control
                                        type="text"
                                        name="lastName"
                                        value={profileForm.lastName}
                                        onChange={handleProfileChange}
                                        disabled={!canEditProfile}
                                    />
                                </Form.Group>

                                <Form.Group className="mb-3">
                                    <Form.Label>Email</Form.Label>
                                    <Form.Control
                                        type="email"
                                        name="email"
                                        value={profileForm.email}
                                        onChange={handleProfileChange}
                                        isInvalid={!!emailError}
                                        disabled={!canEditProfile}
                                    />
                                    <Form.Control.Feedback type="invalid">
                                        {emailError}
                                    </Form.Control.Feedback>
                                </Form.Group>

                                <Form.Group className="mb-3">
                                    <Form.Label>Profile Picture</Form.Label>
                                    <div className="admin-profile-picture-upload">
                                        <div className="admin-profile-picture-preview">
                                            {profilePicturePreview ? (
                                                <img
                                                    src={profilePicturePreview}
                                                    alt="Preview"
                                                    className="admin-profile-picture-img"
                                                />
                                            ) : !profilePictureRemoved && user?.profilePicture ? (
                                                <img
                                                    src={user.profilePicture}
                                                    alt="Profile"
                                                    className="admin-profile-picture-img"
                                                />
                                            ) : (
                                                <div className="admin-profile-picture-placeholder">
                                                    {((user?.firstName || '?')[0] + (user?.lastName || '?')[0]).toUpperCase()}
                                                </div>
                                            )}
                                        </div>
                                        <div className="admin-profile-picture-actions">
                                            <Form.Control
                                                type="file"
                                                accept="image/*"
                                                onChange={handleProfilePictureChange}
                                                className="admin-profile-picture-input"
                                                disabled={!canEditProfile}
                                            />
                                            {!profilePictureFile && user?.profilePicture && !profilePictureRemoved && (
                                                <Button
                                                    variant="outline-danger"
                                                    size="sm"
                                                    onClick={handleRemoveProfilePicture}
                                                    className="admin-profile-picture-remove"
                                                    disabled={!canEditProfile}
                                                >
                                                    <i className="bi bi-trash me-1"></i>Remove
                                                </Button>
                                            )}
                                            {profilePictureFile && (
                                                <Button
                                                    variant="outline-secondary"
                                                    size="sm"
                                                    onClick={() => {
                                                        setProfilePictureFile(null);
                                                        setProfilePictureRemoved(false);
                                                        setProfilePicturePreview(null);
                                                    }}
                                                    className="admin-profile-picture-cancel"
                                                    disabled={!canEditProfile}
                                                >
                                                    Cancel
                                                </Button>
                                            )}
                                        </div>
                                    </div>
                                </Form.Group>
                            </Form>
                        </Tab.Pane>

                        <Tab.Pane eventKey="permissions">
                            {!canManagePermissions && (
                                <Alert variant="warning" className="mb-3">
                                    <i className="bi bi-exclamation-triangle me-2"></i>
                                    You need the <strong>users:managePermissions</strong> permission to manage permissions.
                                </Alert>
                            )}
                            {ALL_PERMISSIONS.map(({ key, label, icon }) => {
                                const impliedByManage = key === 'users:viewPermissions' && selectedPerms.includes('users:managePermissions');
                                const isChecked = selectedPerms.includes(key) || impliedByManage;

                                return (
                                    <Form.Check
                                        key={key}
                                        type="checkbox"
                                        id={`perm-${key}`}
                                        className="mb-2 user-edit-perm-check"
                                        checked={isChecked}
                                        disabled={impliedByManage || !canManagePermissions}
                                        onChange={() => handlePermToggle(key)}
                                        label={
                                            <span>
                                                <i className={`bi ${icon} me-2 text-muted`}></i>
                                                {label}
                                                <code className="ms-2 text-muted perm-key">{key}</code>
                                                {impliedByManage && (
                                                    <span className="ms-2 text-muted perm-implied">(implied)</span>
                                                )}
                                            </span>
                                        }
                                    />
                                );
                            })}
                        </Tab.Pane>
                    </Tab.Content>
                </Tab.Container>
            </Modal.Body>

            <Modal.Footer>
                <Button variant="secondary" onClick={handleClose} disabled={isSaving}>
                    Close
                </Button>
                <Button
                    variant="primary"
                    onClick={handleSave}
                    disabled={isSaving || pictureUploading || (activeTab === 'profile' && !!emailError) || (activeTab === 'profile' && !canEditProfile) || (activeTab === 'permissions' && !canManagePermissions)}
                >
                    {isSaving ? (
                        <>
                            <Spinner
                                as="span"
                                animation="border"
                                size="sm"
                                role="status"
                                aria-hidden="true"
                                className="me-2"
                            />
                            Saving…
                        </>
                    ) : pictureUploading ? (
                        <>
                            <Spinner
                                as="span"
                                animation="border"
                                size="sm"
                                role="status"
                                aria-hidden="true"
                                className="me-2"
                            />
                            Uploading…
                        </>
                    ) : activeTab === 'profile' ? (
                        'Save Profile'
                    ) : (
                        'Save Permissions'
                    )}
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

UserEditModal.propTypes = {
    show: PropTypes.bool.isRequired,
    user: PropTypes.shape({
        username: PropTypes.string.isRequired,
        firstName: PropTypes.string,
        lastName: PropTypes.string,
        email: PropTypes.string,
        profilePicture: PropTypes.string,
        permissions: PropTypes.arrayOf(PropTypes.string),
    }).isRequired,
    onSave: PropTypes.func.isRequired,
    onClose: PropTypes.func.isRequired,
};

export default UserEditModal;
